import React, { useState, useEffect } from "react";
import "./ZReport.css";
import { supabase } from "./supabaseClient";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { DataGrid } from "@mui/x-data-grid";
import { formatCurrency } from "../utils/formatCurrency";
import { createOrUpdateZReport } from "../services/zreportService";

function ZReport() {
  const today = new Date().toISOString().split("T")[0];

  // Form state
  const [date, setDate] = useState(today);
  const [oneStopCard, setOneStopCard] = useState("");
  const [oneStopCash, setOneStopCash] = useState("");

  // Online partners & editing state
  const [partner, setPartner] = useState("");
  const [partnerCard, setPartnerCard] = useState("");
  const [partnerCash, setPartnerCash] = useState("");
  const [onlinePartners, setOnlinePartners] = useState([]);
  const [partnerEditIndex, setPartnerEditIndex] = useState(-1);

  // Snappy Sales Details state
  const [snappyTotalProductSales, setSnappyTotalProductSales] = useState("");
  const [snappyTotalSurcharges, setSnappyTotalSurcharges] = useState("");
  const [snappyRefunds, setSnappyRefunds] = useState("");
  const [snappyCashRefunds, setSnappyCashRefunds] = useState("");
  const [snappyTotalProductSalesFromTill, setSnappyTotalProductSalesFromTill] = useState("");

  // Suppliers & editing state
  const [supplierName, setSupplierName] = useState("");
  const [supplierAmount, setSupplierAmount] = useState("");
  const [supplierMethod, setSupplierMethod] = useState("Cash");
  const [suppliers, setSuppliers] = useState([]);
  const [supplierEditIndex, setSupplierEditIndex] = useState(-1);

  const [deliveryFee, setDeliveryFee] = useState("");
  const [totalCashCounted, setTotalCashCounted] = useState("");

  // Grid & edit tracking
  const [zReports, setZReports] = useState([]);
  const [editId, setEditId] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const safeFixed = (val) => formatCurrency(val);

  // Fetch saved rows and calculate daily variance (not cumulative)
  const fetchReports = async (start, end) => {
    let q = supabase.from("zreports").select("*").order("date", { ascending: true });
    if (start && end) q = q.gte("date", start).lte("date", end);
    const { data, error } = await q;
    if (error) {
      toast.error("Fetch error: " + error.message);
      return;
    }
    let prevCashBalance = 0;
    const reportsWithCalc = data.map((r, idx) => {
      // Calculate net_cash if not present (OneStop + partners - suppliers + delivery_fee)
      let netCash = r.net_cash !== undefined
        ? Number(r.net_cash)
        : (
            Number(r.one_stop_cash) +
            (Array.isArray(r.online_partners)
              ? r.online_partners.reduce((sum, p) => sum + (Number(p.cash) || 0), 0)
              : 0) +
            Number(r.delivery_fee || 0) -
            (Array.isArray(r.suppliers)
              ? r.suppliers.filter(s => s.method === "Cash").reduce((sum, s) => sum + (Number(s.amount) || 0), 0)
              : 0)
          );
      if (isNaN(netCash)) netCash = 0;

      // Cumulative cash balance
      const cash_balance = idx === 0 ? netCash : prevCashBalance + netCash;
      prevCashBalance = cash_balance;

      // Daily variance: counted - cash_balance for this day only
      const totalCounted = Number(r.total_cash_counted) || 0;
      const dailyVariance = totalCounted - cash_balance;

      return {
        ...r,
        net_cash: netCash.toFixed(2),
        cash_balance: cash_balance.toFixed(2),
        variance: dailyVariance.toFixed(2),
        matched: Number(totalCounted) === Number(cash_balance)
      };
    });
    setZReports(reportsWithCalc.reverse());
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line
  }, []);

  // Partner handlers
  const handleAddPartner = () => {
    if (!partner || (!partnerCard && !partnerCash)) {
      toast.error("Enter card or cash for partner.");
      return;
    }
    const entry = { name: partner, card: parseFloat(partnerCard) || 0, cash: parseFloat(partnerCash) || 0 };
    const copy = [...onlinePartners];
    if (partnerEditIndex >= 0) copy[partnerEditIndex] = entry;
    else copy.push(entry);
    setOnlinePartners(copy);
    setPartner(""); setPartnerCard(""); setPartnerCash(""); setPartnerEditIndex(-1);
  };
  const handleEditPartner = (idx) => {
    const p = onlinePartners[idx];
    setPartner(p.name); setPartnerCard(p.card.toString()); setPartnerCash(p.cash.toString());
    setPartnerEditIndex(idx);
  };
  const handleRemovePartner = (idx) => {
    setOnlinePartners(onlinePartners.filter((_, i) => i !== idx));
    if (idx === partnerEditIndex) setPartnerEditIndex(-1);
  };

  // Supplier handlers
  const handleAddSupplier = () => {
    if (!supplierName || !supplierAmount) {
      toast.error("Supplier name & amount required.");
      return;
    }
    const entry = { name: supplierName, amount: parseFloat(supplierAmount) || 0, method: supplierMethod };
    const copy = [...suppliers];
    if (supplierEditIndex >= 0) copy[supplierEditIndex] = entry;
    else copy.push(entry);
    setSuppliers(copy);
    setSupplierName(""); setSupplierAmount(""); setSupplierEditIndex(-1);
  };
  const handleEditSupplier = (idx) => {
    const s = suppliers[idx];
    setSupplierName(s.name); setSupplierAmount(s.amount.toString()); setSupplierMethod(s.method);
    setSupplierEditIndex(idx);
  };
  const handleRemoveSupplier = (idx) => {
    setSuppliers(suppliers.filter((_, i) => i !== idx));
    if (idx === supplierEditIndex) setSupplierEditIndex(-1);
  };

  // Main submit (add or update)
  const handleSubmit = async () => {
    if (!oneStopCard || !oneStopCash || !totalCashCounted) {
      toast.error("OneStop + Total Cash Counted are required.");
      return;
    }
    // prevent duplicate date
    const { data: dup, error: dupErr } = await supabase.from("zreports").select("id").eq("date", date).single();
    if (dupErr && dupErr.code !== "PGRST116") {
      toast.error("Dup check error: " + dupErr.message); return;
    }
    if (!editId && dup) {
      toast.error("Date exists—click Edit."); return;
    }
    const { data: auth, error: authErr } = await supabase.auth.getUser();
    if (authErr) { toast.error("Auth error: " + authErr.message); return; }
    const user = auth.user;
    // Fetch previous day's till value
    const { data: prev, error: prevErr } = await supabase
      .from("zreports")
      .select("snappy_total_product_sales_from_till")
      .lt("date", date)
      .order("date", { ascending: false })
      .limit(1)
      .single();

    const prevTill = prev ? Number(prev.snappy_total_product_sales_from_till) || 0 : 0;
    const todayTill = Number(snappyTotalProductSalesFromTill) || 0;
    const snappyDailyTillSales = todayTill >= prevTill ? todayTill - prevTill : todayTill;
    const snappyProductSales = Number(snappyTotalProductSales) || 0;
    const snappyVariance = snappyProductSales - snappyDailyTillSales;
    const snappyRefundsValue = Number(snappyRefunds) || 0;
    const snappyCashRefundsValue = Number(snappyCashRefunds) || 0;
    const snappyTotalRefunds = snappyRefundsValue + snappyCashRefundsValue;

    // Calculate net_cash for this entry
    let netCash =
      (parseFloat(oneStopCash) || 0) +
      onlinePartners.reduce((sum, p) => sum + (Number(p.cash) || 0), 0) +
      (parseFloat(deliveryFee) || 0) -
      suppliers.filter(s => s.method === "Cash").reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

    // Calculate cash_balance up to this entry (fetch all previous, sum net_cash)
    let cash_balance = netCash;
    const { data: prevRows } = await supabase
      .from("zreports")
      .select("net_cash,date")
      .lt("date", date)
      .order("date", { ascending: true });
    if (prevRows && prevRows.length > 0) {
      cash_balance =
        prevRows.reduce((sum, r) => sum + (Number(r.net_cash) || 0), 0) + netCash;
    }

    // Calculate daily variance
    const dailyVariance = (parseFloat(totalCashCounted) || 0) - cash_balance;

    const newEntry = {
      date,
      one_stop_card:      parseFloat(oneStopCard) || 0,
      one_stop_cash:      parseFloat(oneStopCash) || 0,
      online_partners:    onlinePartners,
      suppliers:          suppliers,
      delivery_fee:       parseFloat(deliveryFee) || 0,
      total_cash_counted: parseFloat(totalCashCounted) || 0,
      // --- Snappy Sales Details ---
      snappy_total_product_sales: snappyProductSales,
      snappy_total_surcharges: Number(snappyTotalSurcharges) || 0,
      snappy_refunds: snappyRefundsValue,
      snappy_cash_refunds: snappyCashRefundsValue,
      snappy_total_refunds: snappyTotalRefunds,
      snappy_total_product_sales_from_till: todayTill,
      snappy_daily_till_sales: snappyDailyTillSales,
      snappy_variance: snappyVariance,
      // --- End Snappy Sales Details ---
      net_cash: netCash,
      cash_balance: cash_balance,
      variance: dailyVariance,
      logged_by:          editId ? undefined : user.user_metadata.display_name,
      logged_at:          editId ? undefined : new Date().toISOString(),
      modified_by:        editId ? user.user_metadata.display_name : undefined,
      modified_at:        editId ? new Date().toISOString() : undefined,
    };

    try {
      await createOrUpdateZReport(newEntry);
      toast.success(editId ? "Updated" : "Added");
      resetForm();
      fetchReports(fromDate, toDate);
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Save error: " + err.message);
    }
  };

  // Recalculate all (just refetch and recalc in UI)
  const handleRecalculate = async () => {
    fetchReports(fromDate, toDate);
    toast.success("All reports recalculated");
  };

  // Update resetForm to clear snappy fields
  const resetForm = () => {
    setDate(today);
    setOneStopCard(""); setOneStopCash("");
    setPartner(""); setPartnerCard(""); setPartnerCash(""); setOnlinePartners([]); setPartnerEditIndex(-1);
    setSupplierName(""); setSupplierAmount(""); setSupplierMethod("Cash"); setSuppliers([]); setSupplierEditIndex(-1);
    setDeliveryFee(""); setTotalCashCounted("");
    setSnappyTotalProductSales("");
    setSnappyTotalSurcharges("");
    setSnappyRefunds("");
    setSnappyCashRefunds("");
    setSnappyTotalProductSalesFromTill("");
    setEditId(null);
  };

  // Update handleEdit to load snappy fields
  const handleEdit = (r) => {
    setDate(r.date);
    setOneStopCard(r.one_stop_card.toString());
    setOneStopCash(r.one_stop_cash.toString());
    setOnlinePartners(r.online_partners || []);
    setSuppliers(r.suppliers || []);
    setDeliveryFee(r.delivery_fee.toString());
    setTotalCashCounted(r.total_cash_counted.toString());
    setSnappyTotalProductSales(r.snappy_total_product_sales?.toString() || "");
    setSnappyTotalSurcharges(r.snappy_total_surcharges?.toString() || "");
    setSnappyRefunds(r.snappy_refunds?.toString() || "");
    setSnappyCashRefunds(r.snappy_cash_refunds?.toString() || "");
    setSnappyTotalProductSalesFromTill(r.snappy_total_product_sales_from_till?.toString() || "");
    setEditId(r.id);
  };

  const handleDelete = async (id) => {
    if (prompt("Type DELETE to confirm") !== "DELETE") { toast.info("Cancelled"); return; }
    const { error } = await supabase.from("zreports").delete().eq("id", id);
    if (error) toast.error("Delete failed: " + error.message);
    else { toast.success("Deleted"); fetchReports(fromDate, toDate); }
  };

  // DataGrid columns
  const columns = [
    { field: 'date', headerName: 'Date', width: 110 },
    { field: 'one_stop_card', headerName: 'OneStop Card', width: 140, renderCell: p => <span className="blue">{safeFixed(p.value)}</span> },
    { field: 'one_stop_cash', headerName: 'OneStop Cash', width: 140, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'snappy_card', headerName: 'Snappy Card', width: 130, renderCell: p => <span className="blue">{safeFixed(p.value)}</span> },
    { field: 'snappy_cash', headerName: 'Snappy Cash', width: 130, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'justeat_card', headerName: 'JustEat Card', width: 130, renderCell: p => <span className="blue">{safeFixed(p.value)}</span> },
    { field: 'justeat_cash', headerName: 'JustEat Cash', width: 130, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'deliveroo_card', headerName: 'Deliveroo Card', width: 140, renderCell: p => <span className="blue">{safeFixed(p.value)}</span> },
    { field: 'deliveroo_cash', headerName: 'Deliveroo Cash', width: 140, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'delivery_fee', headerName: 'Delivery Fee', width: 130, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'cash_in', headerName: 'Cash In', width: 120, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'supplier_bank_out', headerName: 'Supplier Bank', width: 140, renderCell: p => <span className="blue">{safeFixed(p.value)}</span> },
    { field: 'supplier_cash_out', headerName: 'Supplier Cash', width: 140, renderCell: p => <span className="red">{safeFixed(p.value)}</span> },
    { field: 'net_cash', headerName: 'Net Cash', width: 130, renderCell: p => <span className="red">{safeFixed(p.value)}</span> },
    { field: 'cash_balance', headerName: 'Cash Balance', width: 140, renderCell: p => <span className="green">{safeFixed(p.value)}</span> },
    { field: 'total_cash_counted', headerName: 'Total Counted', width: 140, renderCell: p => safeFixed(p.value) },
    {
      field: 'variance',
      headerName: 'Daily Variance',
      width: 150,
      renderCell: p =>
        <span style={{ color: Number(p.value) === 0 ? 'green' : 'crimson' }}>
          {Number(p.value) === 0 ? 'Matched' : safeFixed(p.value)}
        </span>
    },
    { field: 'suppliers', headerName: 'Suppliers', width: 250, renderCell: p => (p.value || []).map(s => `${s.name} £${s.amount} (${s.method})`).join(", ") },
    { field: 'snappy_total_product_sales', headerName: 'Snappy Product Sales (£)', width: 180, renderCell: p => formatCurrency(p.value) },
    { field: 'snappy_total_surcharges', headerName: 'Snappy Surcharges (£)', width: 160, renderCell: p => formatCurrency(p.value) },
    { field: 'snappy_refunds', headerName: 'Snappy Refunds (£)', width: 150, renderCell: p => formatCurrency(p.value) },
    { field: 'snappy_cash_refunds', headerName: 'Snappy Cash Refunds (£)', width: 180, renderCell: p => formatCurrency(p.value) },
    { field: 'snappy_total_refunds', headerName: 'Snappy Total Refunds (£)',  width: 180,  renderCell: p => formatCurrency(p.value)},
    { field: 'snappy_total_product_sales_from_till', headerName: 'Snappy Sales from Till (£)', width: 200, renderCell: p => formatCurrency(p.value) },
    { field: 'snappy_variance',headerName: 'Snappy Variance (£)',width: 180,  renderCell: p => formatCurrency(p.value)},
    { field: 'logged_by', headerName: 'Logged By', width: 140 },
    { field: 'logged_at', headerName: 'Logged At', width: 170, renderCell: p => p.value ? format(new Date(p.value), "dd/MM/yyyy HH:mm") : "" },
    { field: 'modified_by', headerName: 'Modified By', width: 140 },
    { field: 'modified_at', headerName: 'Modified At', width: 170, renderCell: p => p.value ? format(new Date(p.value), "dd/MM/yyyy HH:mm") : "" },
    { field: 'actions', headerName: 'Actions', width: 200, sortable: false, renderCell: p => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn-edit" onClick={() => handleEdit(p.row)}>Edit</button>
          <button className="btn-delete" onClick={() => handleDelete(p.row.id)}>Delete</button>
        </div>
      )}
  ];

  return (
    <div className="main-content zreport-container">
      <div className="main-heading">🧾 Store Z-Report</div>

      {/* Date Section */}
      <div className="form-section">
        <div className="section-title">📅 Date</div>
        <div className="form-grid">
          <label>
            Date
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </label>
        </div>
      </div>

      {/* One Stop Section */}
      <div className="form-section">
        <div className="section-title">🛍️ One Stop</div>
        <div className="form-grid">
          <label>
            Card (£)
            <input type="number" step="0.01" value={oneStopCard} onChange={e => setOneStopCard(e.target.value)} />
          </label>
          <label>
            Cash (£)
            <input type="number" step="0.01" value={oneStopCash} onChange={e => setOneStopCash(e.target.value)} />
          </label>
        </div>
      </div>

      {/* Online Partners Section */}
      <div className="form-section">
        <div className="section-title">🌐 Online Partners</div>
        <div className="form-grid">
          <label>
            Partner
            <select value={partner} onChange={e => setPartner(e.target.value)}>
              <option value="">Select</option>
              <option>Snappy</option>
              <option>Deliveroo</option>
              <option>Just Eat</option>
              <option>Others</option>
            </select>
          </label>
          <label>
            Card (£)
            <input type="number" step="0.01" value={partnerCard} onChange={e => setPartnerCard(e.target.value)} />
          </label>
          <label>
            Cash (£)
            <input type="number" step="0.01" value={partnerCash} onChange={e => setPartnerCash(e.target.value)} />
          </label>
          <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
            <button className="btn-apply" style={{ marginTop: 22 }} onClick={handleAddPartner}>
              {partnerEditIndex >= 0 ? "Update Partner" : "+ Add Partner"}
            </button>
          </div>
        </div>
        <ul>
          {onlinePartners.map((p, i) => (
            <li key={i}>
              {p.name}: Card £{p.card}, Cash £{p.cash}
              <button onClick={() => handleEditPartner(i)}>✎</button>
              <button onClick={() => handleRemovePartner(i)}>🗑️</button>
            </li>
          ))}
        </ul>
        <div className="form-grid" style={{ marginTop: 8 }}>
          <label>
            Delivery Fee (£)
            <input type="number" step="0.01" value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)} />
          </label>
        </div>
      </div>

      {/* Snappy Sales Details Section */}
      <div className="form-section">
        <div className="section-title">🚀 Snappy Sales Details</div>
        <div className="form-grid">
          <label>
            Total Product Sales (£)
            <input
              type="number"
              step="0.01"
              value={snappyTotalProductSales}
              onChange={e => setSnappyTotalProductSales(e.target.value)}
            />
          </label>
          <label>
            Total Surcharges (£)
            <input
              type="number"
              step="0.01"
              value={snappyTotalSurcharges}
              onChange={e => setSnappyTotalSurcharges(e.target.value)}
            />
          </label>
          <label>
            Refunds (£)
            <input
              type="number"
              step="0.01"
              value={snappyRefunds}
              onChange={e => setSnappyRefunds(e.target.value)}
            />
          </label>
          <label>
            Cash Refunds (£)
            <input
              type="number"
              step="0.01"
              value={snappyCashRefunds}
              onChange={e => setSnappyCashRefunds(e.target.value)}
            />
          </label>
          <label>
            Total Product Sales from Till (£)
            <input
              type="number"
              step="0.01"
              value={snappyTotalProductSalesFromTill}
              onChange={e => setSnappyTotalProductSalesFromTill(e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* Supplier Section */}
      <div className="form-section">
        <div className="section-title">🏦 Supplier</div>
        <div className="form-grid">
          <label>
            Name
            <input value={supplierName} onChange={e => setSupplierName(e.target.value)} />
          </label>
          <label>
            Amount (£)
            <input type="number" step="0.01" value={supplierAmount} onChange={e => setSupplierAmount(e.target.value)} />
          </label>
          <label>
            Method
            <select value={supplierMethod} onChange={e => setSupplierMethod(e.target.value)}>
              <option>Cash</option>
              <option>Bank</option>
            </select>
          </label>
          <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
            <button className="btn-supplier" style={{ marginTop: 22 }} onClick={handleAddSupplier}>
              {supplierEditIndex >= 0 ? "Update Supplier" : "+ Add Supplier"}
            </button>
          </div>
        </div>
        <ul>
          {suppliers.map((s, i) => (
            <li key={i}>
              {s.name} £{s.amount} ({s.method})
              <button onClick={() => handleEditSupplier(i)}>✎</button>
              <button onClick={() => handleRemoveSupplier(i)}>🗑️</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Total Cash Counted Section */}
      <div className="form-section">
        <div className="section-title">💷 Total Cash Counted</div>
        <div className="form-grid">
          <label>
            Total Cash Counted (£)
            <input type="number" step="0.01" value={totalCashCounted} onChange={e => setTotalCashCounted(e.target.value)} />
          </label>
          <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
            <button className="btn-entry" style={{ marginTop: 22 }} onClick={handleSubmit}>
              {editId ? "Update Entry" : "Add Entry"}
            </button>
            <button className="btn-apply" style={{ marginTop: 22, background: "#eee", color: "#222" }} onClick={resetForm}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* FILTER & EXPORT */}
      <div className="form-section">
        <div className="section-title">🔍 Filter</div>
        <div className="form-grid">
          <label>
            From
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </label>
          <button className="btn-apply" style={{ marginTop: 22 }} onClick={() => fetchReports(fromDate, toDate)}>
            Apply
          </button>
          <button className="btn-export" style={{ marginTop: 22 }} onClick={() => {
            const hdrs = columns.map(c => c.headerName).join(",");
            const rows = zReports.map(r => columns.map(c => typeof r[c.field] !== "object" ? r[c.field] : "").join(","));
            const blob = new Blob([hdrs + "\n" + rows.join("\n")], { type: "text/csv" });
            saveAs(blob, `zreport_${format(new Date(),"yyyyMMdd_HHmm")}.csv`);
          }}>
            Export CSV
          </button>
          <button className="btn-recalc" style={{ marginTop: 22 }} onClick={handleRecalculate}>
            Recalculate All
          </button>
        </div>
      </div>

      {/* DATA GRID */}
      <div style={{ overflowX: "auto" }}>
        <DataGrid
          rows={zReports}
          columns={columns}
          getRowId={row => row.id}
          pageSize={5}
          rowsPerPageOptions={[5,10,25,50]}
          autoHeight
        />
      </div>
    </div>
  );
}

export default ZReport;